import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import moment from "moment";
import { DateRangePicker, DateRangeValue } from "../../../components/ui/daterange-picker";
import {
  checkInDateSelected,
  checkInDateSelectedClear,
  checkOutDateSelected,
  checkOutDateSelectedClear,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import NumericInput from "../../common/NumberOfCustomers";
import CityAndLocationSelection from "../common/locationSelection/CityAndDistrictSelection";
import {useGetMaxGuestsQuery} from "../../../api/locations/locationsApiSlice";
import {useMemo} from "react";
import { isOutsideBookableDays, useBookingWindow } from "../common/hooks/useBookingWindow";
import { useObjectFirstEntry } from "../common/hooks/useObjectFirstEntry";
import { useHeadCount } from "../common/hooks/useHeadCount";
import PickedRoomSummary from "../common/components/ObjectFirst/PickedRoomSummary";
import { usePickedRoomTimeZone } from "../../../util/locationTimeZone";

const LocationAndDurationSelectionStep = () => {
  const { t } = useTranslation();
  const locations = useSelector(
    (state: RootState) => state.booking.useCaseLocations
  );
  const { config } = useSelector((state: RootState) => state.tenantConfig);

  const { data: guestsMax } = useGetMaxGuestsQuery({ useCase: "apartment" }, {
    refetchOnMountOrArgChange: 60
  });

  const startDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const endDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );

  const dispatch = useDispatch();

  // The booking window this tenant configured for apartments — the lead buffer
  // and the horizon. It replaces `moment().add(5, "days")` and
  // `moment().add(18, "months")`, which were hardcoded here for one tenant and
  // then applied to every tenant on this use case. Those numbers are now the
  // DEFAULTS, so an unconfigured tenant behaves exactly as it did before.
  //
  // ADR-0020: this greys out the dates the app API refuses — the rule itself
  // lives in `backend/src/core/lib/bookingWindow.ts`. The buffers apply to
  // CUSTOMER bookings only; staff creating a booking in the admin area are
  // deliberately not subject to them (PO, 2026-09-20).
  //
  // #3035/#3219: came in object-first? Then this step is the dates for ONE
  // room, on that room's clock — the same resolver as the venue step.
  const { pickedRoom } = useObjectFirstEntry("apartment");
  const timeZone = usePickedRoomTimeZone(pickedRoom?.locationId);
  const bookingWindow = useBookingWindow("apartment", timeZone);

  // The minimum stay. A pinned build (REACT_APP_TENANT) never reads the runtime
  // config, so its bundled `minimumNumberOfDaysForBooking` still decides there;
  // on the shared app the tenant's own setting in Services does, and the API
  // refuses a shorter stay (`isShorterThanMinStay`, backend). Default 1 night.
  const minNights = config.minimumNumberOfDaysForBooking ?? bookingWindow.minNights;
  // Calendar-day labels, not a phone midnight against Berlin instants.
  const isDateDisabled = (d: Date) =>
    isOutsideBookableDays(moment(d), bookingWindow.earliest, bookingWindow.latest);

  const handleRangeChange = (range: DateRangeValue | null) => {
    if (!range || !range.from) {
      dispatch(checkInDateSelectedClear());
      dispatch(checkOutDateSelectedClear());
      return;
    }
    const fromMoment = moment(range.from).startOf("day");
    dispatch(checkInDateSelected({ checkInDate: fromMoment }));
    // react-day-picker emits {from: X, to: X} on the first click in mode="range".
    // Treat to === from as "still picking" — only the second click on a
    // different day should commit a check-out date and trigger the
    // minimumNumberOfDaysForBooking enforcement.
    if (!range.to || moment(range.to).isSame(fromMoment, "day")) {
      dispatch(checkOutDateSelectedClear());
      return;
    }
    const toMoment = moment(range.to).startOf("day");
    const minEnd = fromMoment.clone().add(minNights, "day");
    dispatch(
      checkOutDateSelected({
        checkOutDate: toMoment.isBefore(minEnd) ? minEnd : toMoment,
      })
    );
  };

  const dateRangeValue: DateRangeValue = useMemo(
    () => ({
      from: startDate ? startDate.toDate() : null,
      to: endDate ? endDate.toDate() : null,
    }),
    [startDate, endDate]
  );

  const roomRentalLocation = useSelector(
      (state: RootState) => state.bookingCreation.roomRentalLocation
  )

  const currentGuestsMax: number = useMemo(() => {
    if (guestsMax && roomRentalLocation?.city) {
      return guestsMax[roomRentalLocation.city] || 100;
    }
    return 100
  }, [roomRentalLocation, guestsMax])

  const headCount = useHeadCount("apartment", currentGuestsMax, pickedRoom);

  // Where the booking is: the picked room when the customer came in
  // object-first (#3035), the city/district choice otherwise.
  const renderWhere = () => {
    if (pickedRoom?.city) return <PickedRoomSummary room={pickedRoom} />;
    if (locations) return <CityAndLocationSelection locations={locations} />;
    return <p className="text-gray-700">{t("book_location.no_location")}</p>;
  };

  return (
    <div className="flex flex-col w-full max-w-2xl gap-6 px-3 pb-12 mx-auto sm:px-5">
      <div className="w-full">
        {renderWhere()}
      </div>

      <div className="w-full flex flex-col gap-2">
        <label className="text-sm font-medium text-slate-900">
          {t("apartment.check_in")} – {t("apartment.check_out")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <DateRangePicker
          value={dateRangeValue}
          onChange={handleRangeChange}
          isDateDisabled={isDateDisabled}
          defaultMonth={bookingWindow.earliest.toDate()}
          buttonClassName="w-full h-[62px] text-base border rounded-[12px] bg-white text-slate-900 border-primary hover:border-primary"
          aria-label={`${t("apartment.check_in")} – ${t("apartment.check_out")}`}
          clearable
        />
        <p className="text-xs text-slate-900">
          {t("book_location.earliest_available_date", {
            date: bookingWindow.earliest.format("LL"),
          })}
        </p>
        {minNights > 1 && (
          <p className="text-xs text-slate-900">
            {t("book_location.min_stay_hint", { count: minNights })}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 sm:gap-4">
        <span className="text-base font-normal text-gray-600">
          {t(headCount.labelKey)}
        </span>
        <NumericInput max={headCount.max} labelKey={headCount.labelKey} />
      </div>
    </div>
  );
};

export default LocationAndDurationSelectionStep;
